import { Alert, Box, Divider, Group, Loader, Stack } from "@mantine/core";
import {
  Navigate,
  Outlet,
  useMatch,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useEffect, useRef } from "react";
import { ORDER_STEPS, OrderStepper, OrderSteps } from "./OrderStepper";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { ORDER_FORM_V2_FLAG } from "@jasper/product-workflows";
import { useOrderConfiguration } from "@jasper/use-cases";
import { PatientBanner } from "./PatientBanner";

const OrderConfigurationHeader = ({
  activeSteps,
  orderId,
  patient,
  reference,
}: {
  activeSteps: OrderSteps;
  orderId: string;
  patient?: {
    firstName: string;
    lastName: string;
    birthDate?: Date | null;
    sex?: string | null;
  };
  reference: string;
}) => {
  const navigate = useNavigate();

  return (
    <Stack gap={0} pos="sticky" top={0} style={{ zIndex: 10 }}>
      <Stack align="center" p="md" bg="cardBackground">
        <OrderStepper activeStep={activeSteps} />
      </Stack>

      <Divider />
      <PatientBanner
        patient={patient}
        reference={reference}
        onEdit={() => navigate(`/orders/${orderId}/configure/order-info`)}
      />

      <Divider />
    </Stack>
  );
};

const OrderConfigurationLayoutContent = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const matches = useMatch("/orders/:orderId/configure/:orderStep/*");
  const initialIsEditableRef = useRef<boolean | null>(null);

  const { order, isLoading, error, isEditable } = useOrderConfiguration({
    orderId: orderId!,
  });

  const orderStep = matches?.params.orderStep;
  const isConfirmationPage = orderStep === "confirmation";
  const orderStepIndex = ORDER_STEPS.indexOf(
    orderStep as (typeof ORDER_STEPS)[number]
  );

  useEffect(() => {
    initialIsEditableRef.current = null;
  }, [orderId]);

  useEffect(() => {
    if (initialIsEditableRef.current === null && isEditable !== undefined) {
      initialIsEditableRef.current = isEditable;
    }
  }, [isEditable]);

  useEffect(() => {
    if ((!orderStep || orderStepIndex === -1) && !isConfirmationPage) {
      navigate(`/orders/${orderId}/configure/${ORDER_STEPS[0]}`);
    }
  }, [orderStep, orderStepIndex, navigate, orderId, isConfirmationPage]);

  if (error) {
    return (
      <Stack h="100%" w="100%" justify="center" align="center">
        <Alert color="red">
          Une erreur est survenue lors du chargement de la commande.
        </Alert>
      </Stack>
    );
  }

  if (
    isLoading ||
    !order ||
    !orderStep ||
    (orderStepIndex === -1 && !isConfirmationPage)
  ) {
    return (
      <Stack h="100%" w="100%" justify="center" align="center">
        <Loader />
      </Stack>
    );
  }

  if (initialIsEditableRef.current === false) {
    return <Navigate to={`/order/${orderId}`} replace />;
  }

  return (
    <Stack gap={0} h="100%">
      <OrderConfigurationHeader
        orderId={orderId!}
        activeSteps={ORDER_STEPS[orderStepIndex]}
        patient={order.patient}
        reference={order.reference}
      />
      <Group gap={0} h="100%" align="flex-start">
        <Box flex={1} h="100%">
          <Outlet context={{ orderId }} />
        </Box>
      </Group>
    </Stack>
  );
};

export const OrderConfigurationLayout = () => {
  const isOrderFormV2Enabled = useFeatureFlagEnabled(ORDER_FORM_V2_FLAG);

  if (isOrderFormV2Enabled === undefined) {
    return null;
  }

  if (isOrderFormV2Enabled === false) {
    return <Navigate to="/" replace />;
  }

  return <OrderConfigurationLayoutContent />;
};
